import {API_BASE_URL} from '../constants';

/**
 * Trades the link token in the URL fragment for a short-lived session, held for the
 * life of the page. Nothing is persisted: the session dies with the tab, and the token
 * stays in the fragment, which no request ever carries.
 */

export const SHARE_SESSION_HEADER = 'X-Share-Session';

/** The fragment key minted by the API: `/share#t={token}`. */
const FRAGMENT_TOKEN_KEY = 't';

export interface SharedProject {
  id: number;
  slug: string;
  workspace_id: number;
  workspace_slug: string;
  company_id: number;
  company_slug: string;
}

export interface ShareExchange {
  session_token: string;
  expires_in: number;
  target_url: string;
  project: SharedProject;
}

let sessionToken: string | null = null;
let linkToken: string | null = null;
let sharedProject: SharedProject | null = null;

/** Shared by concurrent callers, so one stale session cannot burst the exchange. */
let inFlightExchange: Promise<boolean> | null = null;

/**
 * posthog-js builds `$current_url` from `window.location.href`, fragment included, and
 * `capture_pageleave`/`capture_exceptions` are on -- so without this a share viewer
 * ships a live ninety-day credential to a third-party sink.
 */
export function redactShareTokenFromUrl(value: string): string {
  const hashIndex = value.indexOf('#');

  if (hashIndex === -1) return value;

  const fragment = new URLSearchParams(value.slice(hashIndex + 1));

  if (!fragment.get(FRAGMENT_TOKEN_KEY)) return value;

  fragment.set(FRAGMENT_TOKEN_KEY, 'redacted');

  return `${value.slice(0, hashIndex)}#${fragment.toString()}`;
}

export function readShareTokenFromHash(hash: string): string | null {
  // `URLSearchParams` on the fragment body: it handles the `#t=x&y=z` shape and the
  // percent-decoding without this file owning a parser.
  const token = new URLSearchParams(hash.replace(/^#/, '')).get(
    FRAGMENT_TOKEN_KEY,
  );

  return token || null;
}

/** The link token this page was opened with, from memory or from the current URL. */
function currentLinkToken(): string | null {
  return linkToken ?? readShareTokenFromHash(window.location?.hash ?? '');
}

export function getShareSessionToken(): string | null {
  return sessionToken;
}

/**
 * The application resolves a project by walking company -> workspace -> project slug,
 * every step of which a grant may not call. The exchange answers it in one response.
 */
export function getSharedProject(): SharedProject | null {
  return sharedProject;
}

/** True from the fragment onwards, before any session exists -- a reload has one but
 * not the other, and must not be mistaken for an unauthenticated visitor. */
export function isShareLinkView(): boolean {
  return sessionToken !== null || currentLinkToken() !== null;
}

export function clearShareSession(): void {
  sessionToken = null;
  linkToken = null;
  sharedProject = null;
  inFlightExchange = null;
}

/**
 * A bare `fetch`, not `callApi`: the latter reaches for a Clerk token and redirects to
 * sign-in when there is none, which is what must not happen to a visitor with no
 * account.
 */
export async function exchangeShareLink(token: string): Promise<ShareExchange> {
  const response = await fetch(
    `${API_BASE_URL}/v1/public/share-links/exchange`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      // In the body, never the query string -- see `redactShareTokenFromUrl`.
      body: JSON.stringify({token}),
    },
  );

  if (!response.ok) {
    throw new Error('This share link is no longer valid.');
  }

  const exchange: ShareExchange = await response.json();

  sessionToken = exchange.session_token;
  linkToken = token;
  sharedProject = exchange.project;

  return exchange;
}

/** False when there is nothing to renew from, or the link itself is gone. */
export async function renewShareSession(): Promise<boolean> {
  if (inFlightExchange) {
    return inFlightExchange;
  }

  const token = currentLinkToken();

  if (!token) {
    return false;
  }

  inFlightExchange = exchangeShareLink(token)
    .then(() => true)
    .catch(() => {
      sessionToken = null;
      sharedProject = null;
      return false;
    })
    .finally(() => {
      inFlightExchange = null;
    });

  return inFlightExchange;
}
